import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Download, FileSpreadsheet, Loader2, Upload } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import {
  ImportRefusedError,
  registrationsService,
  type ImportResult,
  type Registration,
  type RegistrationCategory,
  type RegistrationTeam,
} from '../../services/registrations';
import {
  downloadTemplate,
  previewImport,
  readEntrySheet,
  type ImportPreview,
  type PreviewRow,
} from '../entry-sheet';

interface ImportEntriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stageSlug: string;
  /** What is entered already, so the preview can say which rows correct whom. */
  registrations: Registration[];
  categories: RegistrationCategory[];
  teams: RegistrationTeam[];
  /**
   * Whether an entry here may hold the gender of its class. When it may not,
   * a gender column is not read — here or on the server — and the desk is told.
   */
  genderCollected?: boolean;
  /** Some class here is split over turns: the dialog says a file books none. */
  turnsOffered?: boolean;
  /** The competition takes an entry fee: the template keeps its "paid" column. */
  acceptsPayments?: boolean;
  onImported: (result: ImportResult) => void;
}

/**
 * A spreadsheet into the entry list, with a look first.
 *
 * The file is read here in the browser and shown back as what it will do —
 * which rows enter somebody, which correct an entry, which cannot go in — and
 * only then sent, as rows. That look is not decoration: entering somebody
 * emails them, and a club's file of forty names is forty emails, so the desk
 * sees the count before anything is written.
 *
 * The server refuses the whole file when any row is wrong, and its reasons
 * come back onto the same rows here, so the sheet can be fixed and sent again
 * without anything half-done in between.
 */
export default function ImportEntriesDialog({
  open,
  onOpenChange,
  stageSlug,
  registrations,
  categories,
  teams,
  genderCollected = true,
  turnsOffered = false,
  acceptsPayments = false,
  onImported,
}: ImportEntriesDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [unknownHeaders, setUnknownHeaders] = useState<string[]>([]);
  const [hasIdentity, setHasIdentity] = useState(true);
  // The sheet has a gender column the organiser's agreement does not cover.
  const [genderIgnored, setGenderIgnored] = useState(false);
  const [importing, setImporting] = useState(false);
  // What the server said, when it said no: one line per row, on top of the
  // preview's own checks.
  const [refusal, setRefusal] = useState<string[]>([]);

  useEffect(() => {
    if (open) return;
    setFileName(null);
    setPreview(null);
    setUnknownHeaders([]);
    setHasIdentity(true);
    setGenderIgnored(false);
    setRefusal([]);
  }, [open]);

  const handleFile = async (file: File) => {
    setReading(true);
    setRefusal([]);
    setFileName(file.name);
    try {
      const parsed = await readEntrySheet(file, t);
      setUnknownHeaders(parsed.unknownHeaders);
      // A way to name somebody: an address, the id an export wrote, or a
      // guardian's address — a file of children under 14 has no athlete email
      // at all, and every row of it is still perfectly nameable.
      setHasIdentity(
        parsed.fields.includes('email') ||
          parsed.fields.includes('id') ||
          parsed.fields.includes('guardian_email')
      );
      setGenderIgnored(!genderCollected && parsed.fields.includes('gender'));
      setPreview(previewImport(parsed, registrations, categories, teams, t, genderCollected));
    } catch {
      setPreview(null);
      toast({ title: t('gymAdmin.registrations.importReadError'), variant: 'destructive' });
    } finally {
      setReading(false);
    }
  };

  const handleImport = async () => {
    if (!preview || preview.errors > 0 || importing) return;

    setImporting(true);
    try {
      const result = await registrationsService.import(
        stageSlug,
        preview.rows.map((r) => r.row)
      );
      onImported(result);
      toast({
        title: t('gymAdmin.registrations.importDone', {
          created: result.created,
          updated: result.updated,
        }),
      });
      onOpenChange(false);
    } catch (error) {
      if (error instanceof ImportRefusedError) {
        // Put each reason on its row where the preview can show it; whatever
        // names no row is listed above the table instead.
        const byRow = new Map<number, string>();
        const general: string[] = [];
        for (const problem of error.rows) {
          if (problem.row === null) general.push(problem.message);
          else if (!byRow.has(problem.row)) byRow.set(problem.row, problem.message);
        }
        setPreview((current) =>
          current
            ? {
                ...current,
                rows: current.rows.map((r) =>
                  byRow.has(r.row.row) ? { ...r, error: byRow.get(r.row.row) } : r
                ),
                errors: current.rows.filter((r) => r.error || byRow.has(r.row.row)).length,
              }
            : current
        );
        setRefusal([t('gymAdmin.registrations.importRefused'), ...general]);
        return;
      }
      toast({
        title: t('gymAdmin.registrations.importError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setImporting(false);
    }
  };

  const handleTemplate = async () => {
    try {
      await downloadTemplate(categories, teams, t, { payments: acceptsPayments });
    } catch {
      toast({ title: t('errors.unexpectedError'), variant: 'destructive' });
    }
  };

  const readyRows = preview ? preview.rows.length : 0;
  const canImport = !!preview && readyRows > 0 && preview.errors === 0 && hasIdentity && !importing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t('gymAdmin.registrations.importTitle')}</DialogTitle>
          <DialogDescription>{t('gymAdmin.registrations.importDescription')}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            className="sr-only"
            data-testid="entry-sheet-input"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
              e.target.value = '';
            }}
          />
          <Button
            type="button"
            variant="outline"
            className="gap-2"
            disabled={reading}
            onClick={() => inputRef.current?.click()}
          >
            {reading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="size-4" />
            )}
            {t('gymAdmin.registrations.importChooseFile')}
          </Button>
          {fileName && <span className="text-sm text-muted-foreground">{fileName}</span>}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto gap-2"
            onClick={handleTemplate}
          >
            <Download className="size-4" />
            {t('gymAdmin.registrations.importTemplate')}
          </Button>
        </div>

        {preview && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span data-testid="import-summary">
                {t('gymAdmin.registrations.importSummary', {
                  created: preview.created,
                  updated: preview.updated,
                  errors: preview.errors,
                })}
              </span>
              {preview.created > 0 && preview.errors === 0 && (
                <span className="text-xs text-muted-foreground">
                  {t('gymAdmin.registrations.importEmailsHint', { count: preview.created })}
                </span>
              )}
            </div>

            {!hasIdentity && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {t('gymAdmin.registrations.importNoIdentity')}
              </p>
            )}

            {readyRows === 0 && hasIdentity && (
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {t('gymAdmin.registrations.importNoRows')}
              </p>
            )}

            {unknownHeaders.length > 0 && (
              <p className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
                {t('gymAdmin.registrations.importUnknownColumns', {
                  columns: unknownHeaders.join(', '),
                })}
              </p>
            )}

            {genderIgnored && (
              <p
                className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
                data-testid="import-gender-ignored"
              >
                {t('gymAdmin.registrations.importGenderIgnored')}
              </p>
            )}

            {/* A file books no turn: whoever lands in a class split over two
                or more comes in with their turn to choose. */}
            {turnsOffered && (
              <p
                className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
                data-testid="import-turns-hint"
              >
                {t('gymAdmin.registrations.importTurnsHint')}
              </p>
            )}

            {refusal.length > 0 && (
              <div
                className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive"
                role="alert"
              >
                {refusal.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </div>
            )}

            {readyRows > 0 && (
              <div className="max-h-[50svh] overflow-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-14">{t('gymAdmin.registrations.importRow')}</TableHead>
                      <TableHead>{t('gymAdmin.members.fields.name')}</TableHead>
                      <TableHead>{t('gymAdmin.members.fields.surname')}</TableHead>
                      <TableHead>{t('gymAdmin.members.fields.email')}</TableHead>
                      <TableHead>{t('gymAdmin.members.fields.category')}</TableHead>
                      <TableHead>{t('gymAdmin.registrations.importStatus')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.map((entry) => (
                      <PreviewLine key={entry.row.row} entry={entry} />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" className="gap-2" disabled={!canImport} onClick={handleImport}>
            {importing ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
            {t('gymAdmin.registrations.importConfirm', { count: readyRows })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the letter about this row will go to, when the athlete has no address:
 * the guardian's, labelled as such so the desk does not read it as the
 * child's own.
 */
function guardianAddress(
  row: PreviewRow['row'],
  existing: PreviewRow['existing'],
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const email = row.guardian_email?.trim() || existing?.guardian_email;
  return email ? t('gymAdmin.registrations.guardianEmailShort', { email }) : '';
}

function PreviewLine({ entry }: { entry: PreviewRow }) {
  const { t } = useTranslation();
  const { row, status, existing, error } = entry;

  return (
    <TableRow data-testid="import-preview-row" data-state={error ? 'error' : status}>
      <TableCell className="text-muted-foreground">{row.row}</TableCell>
      <TableCell>{row.name || existing?.name || ''}</TableCell>
      <TableCell>{row.surname || existing?.surname || ''}</TableCell>
      {/* A child under 14 has no address of their own; what the letter will
          go to is the guardian's, so that is what the desk is shown. */}
      <TableCell className="max-w-56 truncate">
        {row.email || existing?.email || guardianAddress(row, existing, t)}
      </TableCell>
      <TableCell>{row.category || existing?.category_name || ''}</TableCell>
      <TableCell>
        {error ? (
          <span className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertTriangle className="size-3.5 shrink-0" aria-hidden />
            {error}
          </span>
        ) : (
          <Badge variant={status === 'new' ? 'default' : 'secondary'}>
            {status === 'new'
              ? t('gymAdmin.registrations.importStatusNew')
              : t('gymAdmin.registrations.importStatusUpdate')}
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
}
